import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';

interface AppUpdate {
  /** Er staat een nieuwe versie klaar */
  updateAvailable: boolean;
  /** De melding onderin is weggeklikt ("Later"); het menu blijft de update wel tonen */
  toastDismissed: boolean;
  dismissToast: () => void;
  /** Laadt de nieuwe versie */
  update: () => void;
}

const UpdateContext = createContext<AppUpdate>({
  updateAvailable: false,
  toastDismissed: false,
  dismissToast: () => {},
  update: () => {},
});

/**
 * Registreert de service worker één keer en deelt de updatestatus met de update-melding
 * (UpdateToast) en het menu (VersionInfo). Controleert bij starten, bij terugkeren naar de app,
 * zodra de verbinding terug is en elk uur. Herlaadt nooit automatisch.
 */
export function UpdateProvider({ children }: { children: ReactNode }) {
  const [toastDismissed, setToastDismissed] = useState(false);
  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_url, registration) {
      if (!registration) return;
      const check = () => {
        if (navigator.onLine) registration.update().catch(() => {});
      };
      check();
      document.addEventListener('visibilitychange', () => document.visibilityState === 'visible' && check());
      window.addEventListener('online', check);
      setInterval(check, 60 * 60 * 1000);
    },
  });

  const dismissToast = useCallback(() => setToastDismissed(true), []);
  const update = useCallback(() => void updateServiceWorker(true), [updateServiceWorker]);
  const value = useMemo(
    () => ({ updateAvailable: needRefresh, toastDismissed, dismissToast, update }),
    [needRefresh, toastDismissed, dismissToast, update],
  );

  return <UpdateContext.Provider value={value}>{children}</UpdateContext.Provider>;
}

export const useAppUpdate = () => useContext(UpdateContext);
